import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, Sun, Youtube, Instagram, Twitter, Facebook } from 'lucide-react';

const UCLA: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UCLA";

    return (
        <div className="min-h-screen bg-white font-sans text-[#2774AE] selection:bg-[#FFD100] selection:text-[#2774AE]">
             {/* Utility Bar */}
             <div className="bg-[#f1f1f1] px-6 py-2 flex justify-end gap-4 text-xs font-bold text-[#666]">
                 <a href="#" className="hover:text-[#2774AE]">Students</a>
                 <a href="#" className="hover:text-[#2774AE]">Faculty & Staff</a>
                 <a href="#" className="hover:text-[#2774AE]">Alumni</a>
                 <a href="#" className="hover:text-[#2774AE]">Parents</a>
             </div>

            {/* Nav */}
            <nav className="bg-white py-4 px-6 md:px-12 shadow-sm sticky top-0 z-50">
                <div className="max-w-[1440px] mx-auto flex justify-between items-center">
                    <div className="font-bold text-4xl tracking-tighter italic text-[#2774AE]">{schoolName}</div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#333] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#2774AE]">About</a>
                        <a href="#" className="hover:text-[#2774AE]">Academics</a>
                        <a href="#" className="hover:text-[#2774AE]">Admissions</a>
                        <a href="#" className="hover:text-[#2774AE]">Research</a>
                        <a href="#" className="hover:text-[#2774AE]">Campus Life</a>
                        <a href="#" className="hover:text-[#2774AE]">Health</a>
                    </div>
                    
                    <div className="flex gap-4">
                        <Search className="text-[#2774AE] cursor-pointer" size={24} />
                        <Menu className="text-[#2774AE] cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Optimist Campaign */}
            <header className="relative h-[700px]">
                <img 
                    src="https://images.unsplash.com/photo-1549651586-dae37a28e378?q=80&w=2070&auto=format&fit=crop" 
                    alt="UCLA Royce Hall" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#2774AE]/90 via-[#2774AE]/40 to-transparent"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                    <div className="max-w-[700px] text-white">
                        <h1 className="text-6xl md:text-8xl font-bold italic mb-6 leading-none">
                            Impossibility <br/>
                            <span className="text-[#FFD100]">Is An Opinion.</span>
                        </h1>
                        <p className="text-xl md:text-2xl font-light mb-10 max-w-lg leading-relaxed">
                            As a public research university, our mission is the creation, dissemination, preservation and application of knowledge for the betterment of our global society.
                        </p>
                        <Button className="bg-[#FFD100] hover:bg-[#ffe04d] text-[#2774AE] font-bold text-lg px-10 h-14 rounded-none uppercase tracking-wider">
                            Explore {schoolName}
                        </Button>
                    </div>
                </div>
            </header>

            {/* Quick Links with Icons */}
            <section className="bg-[#2774AE] text-white py-12 px-6">
                <div className="max-w-[1440px] mx-auto flex flex-wrap justify-center gap-12 font-bold uppercase tracking-widest text-sm text-center">
                    <a href="#" className="flex flex-col items-center gap-3 hover:text-[#FFD100] transition-colors group">
                        <Sun size={32} className="group-hover:animate-spin-slow" />
                        Campus Life
                    </a>
                    <a href="#" className="flex flex-col items-center gap-3 hover:text-[#FFD100] transition-colors group">
                         <div className="h-8 w-8 border-2 border-white rounded-full flex items-center justify-center font-serif text-lg group-hover:border-[#FFD100]">?</div>
                         Apply Now
                    </a>
                     <a href="#" className="flex flex-col items-center gap-3 hover:text-[#FFD100] transition-colors group">
                         <div className="h-8 w-8 border-2 border-white flex items-center justify-center font-sans text-lg group-hover:border-[#FFD100]">$</div>
                         Financial Aid
                    </a>
                </div>
            </section>

            {/* News Grid - Sunny & Bright */}
            <section className="py-24 px-6 md:px-12 bg-[#fafafa]">
                 <div className="max-w-[1440px] mx-auto">
                     <h2 className="text-4xl font-bold text-[#333] mb-12 italic">{schoolName} Newsroom</h2>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                         <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer group">
                             <div className="aspect-[16/9] overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="News 1"/>
                             </div>
                             <div className="p-8">
                                 <span className="text-[#2774AE] font-bold uppercase text-xs tracking-widest mb-2 block">Health</span>
                                 <h3 className="text-xl font-bold mb-4 group-hover:text-[#2774AE] transition-colors">{schoolName} Health ranked #1 in Los Angeles and California</h3>
                                 <p className="text-gray-600 text-sm">Providing the best care to our diverse community.</p>
                             </div>
                         </div>

                         <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer group">
                             <div className="aspect-[16/9] overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="News 2"/>
                             </div>
                             <div className="p-8">
                                 <span className="text-[#2774AE] font-bold uppercase text-xs tracking-widest mb-2 block">Arts</span>
                                 <h3 className="text-xl font-bold mb-4 group-hover:text-[#2774AE] transition-colors">School of the Arts and Architecture presents fall exhibitions</h3>
                                 <p className="text-gray-600 text-sm">Showcasing student work from across disciplines.</p>
                             </div>
                         </div>

                         <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer group">
                             <div className="aspect-[16/9] overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1507413245164-6160d8298b31?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="News 3"/>
                             </div>
                             <div className="p-8">
                                 <span className="text-[#2774AE] font-bold uppercase text-xs tracking-widest mb-2 block">Science</span>
                                 <h3 className="text-xl font-bold mb-4 group-hover:text-[#2774AE] transition-colors">Astronomers discover new class of cosmic explosion</h3>
                                 <p className="text-gray-600 text-sm">Findings shed light on the life cycle of massive stars.</p>
                             </div>
                         </div>
                     </div>
                 </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#222] text-white py-16 px-6 md:px-12">
                <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div>
                        <div className="text-3xl font-bold italic mb-6">{schoolName}</div>
                        <p className="text-gray-400 text-sm max-w-xs">
                            {schoolName} <br/>
                            Los Angeles, CA 90095
                        </p>
                        <div className="flex gap-4 mt-6">
                            <Twitter size={20} className="hover:text-[#FFD100] cursor-pointer" />
                            <Facebook size={20} className="hover:text-[#FFD100] cursor-pointer" />
                            <Instagram size={20} className="hover:text-[#FFD100] cursor-pointer" />
                            <Youtube size={20} className="hover:text-[#FFD100] cursor-pointer" />
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-sm text-gray-400 font-bold">
                        <ul className="space-y-3">
                            <li className="text-white uppercase tracking-widest text-xs mb-2">General</li>
                            <li><a href="#" className="hover:text-white">Directory</a></li>
                            <li><a href="#" className="hover:text-white">Maps</a></li>
                            <li><a href="#" className="hover:text-white">Careers</a></li>
                        </ul>
                        <ul className="space-y-3">
                            <li className="text-white uppercase tracking-widest text-xs mb-2">Legal</li>
                            <li><a href="#" className="hover:text-white">Privacy</a></li>
                            <li><a href="#" className="hover:text-white">Accessibility</a></li>
                            <li><a href="#" className="hover:text-white">Terms of Use</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1440px] mx-auto mt-12 pt-8 border-t border-gray-800 text-center text-xs text-gray-500">
                    © 2026 Regents of {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default UCLA;
